import { Icon, Modal } from '@deity-io/portal-ui';
import CreateMappingFormFields from 'components/Mappings/CreateMappingFormFields';
import { CreateStructureInputSchema } from 'components/index';
import { useFormik } from 'formik';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { GetPackageVersionContentInfoDocument, useCreateMappingMutation } from 'services/graphql/generated/hooks';
import { v4 } from 'uuid';
import * as Yup from 'yup';

interface CreateMappingModalProps {
  isOpen?: boolean;
  versionUuid: string;
  setOpenState: (state: boolean) => void;
  onMappingCreated: () => void;
}

export interface CreateMappingInput {
  name: string;
  description: string;
  sourceModel: any;
  targetModel: any;
}

export const CreateMappingModal: FC<CreateMappingModalProps> = ({
  isOpen = false,
  versionUuid,
  setOpenState,
  onMappingCreated
}) => {
  const modalRef = useRef<any | null>(null);
  const [create, { data: createData, loading: createLoading }] = useCreateMappingMutation({
    refetchQueries: [
      {
        query: GetPackageVersionContentInfoDocument,
        variables: {
          versionUuid
        }
      }
    ]
  });

  const CreateMappingInputSchema = Yup.object().shape({
    name: Yup.string().min(2, 'Too short').max(50, 'Too long').required('This is a required field.'),
    description: Yup.string().min(2, 'Too short').max(50, 'Too long').required('This is a required field.'),
    sourceModel: Yup.mixed().required('A source model must be selected.'),
    targetModel: Yup.mixed().required('A target model must be selected.')
  });

  useEffect(() => {
    if (isOpen) {
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [isOpen]);

  const fetchParameters = (packageVersionUuid: string) => ({
    variables: {
      offset: 0,
      limit: 20,
      packageVersionUuid
    }
  });

  useEffect(() => {
    if (createData) {
      onMappingCreated();
      modalRef.current?.close();
    }
  }, [createData]);

  const createMapping = (input: CreateMappingInput) => {
    create({
      variables: {
        uuid: v4(),
        packageVersionUuid: versionUuid,
        name: input.name,
        description: input.description,
        schema: {
          _meta: {
            type: 'mapping',
            source: input.sourceModel,
            target: input.targetModel
          }
        }
      }
    });
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      targetModel: null,
      sourceModel: null
    },
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: CreateStructureInputSchema,
    onSubmit: values => createMapping(values)
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setOpenState(false)}
      label="New Mapping"
      title="Enter a name and description."
      closeIcon={<Icon icon="close" />}
      confirmText="Create Mapping"
      onConfirm={() => formik.handleSubmit()}
    >
      <form onSubmit={formik.handleSubmit}>
        <CreateMappingFormFields
          packageVersionUuid={versionUuid}
          errors={formik.errors}
          values={formik.values}
          setFieldValue={formik.setFieldValue}
          setFieldTouched={formik.setFieldTouched}
        />
      </form>
    </Modal>
  );
};
